import { useEffect, useState } from 'react';
import { Dropdown } from '../../Dropdown';
import { useTranslation } from 'react-i18next';
import chatStore from 'shared/store/chatStore';
import { riskProfileMapEngToIta, riskProfileMapItaToEng, parseValue } from 'utils/misc';
import { GridItem } from './GridItem';
import colorsStore from 'shared/store/colorsStore';
import mainStyles from 'style/main.module.scss';
import { formatItalianNumber } from 'utils/formatUtils';
import { IconNote } from 'shared/components/ui/IconNote';
import constraintsStore from 'shared/store/constraintsStore';
import activityPanelStore from 'shared/store/activityPanelStore';

import constraintConfig from 'config/constraints-config.json';

type RiskContentProps = {
  configData: {
    financial_plan: { values: number[]; type: string; default: number };
    risk_profile: { values: string[]; type: string; default: string };
  };
};

const value_at_risk_limits = constraintConfig.constraints[0].value_at_risk_limits;

type RiskActiveValue = {
  financial_plan: number;
  risk_profile: string;
  target_var?: any;
  target_var_min?: any;
  target_var_max?: any;
  align_to_plan?: boolean;
  [key: string]: any;
};

// Helper: convert a numeric VaR value to the string used in a text input.
const formatVarText = (value: any): string => {
  if (value == null || value === '') return '';
  return String(value);
};

export function RiskContent({ configData }: RiskContentProps) {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const ptfThread = activityPanelStore((state) => state.ptfThread);

  const mode = constraintsStore((s) => s.mode);
  const isRebalancing = mode !== 'builder';

  // In rebalancing mode, the max financial plan comes from the imported portfolio (SSO), not from saved constraints
  const threadMetadata = chatStore((state) => state.threadMetadata);
  const importedPortfolio = threadMetadata?.['fineco:fineco_imported_portfolio'] as Record<string, any> | undefined;
  const ssoFinancialPlan = importedPortfolio?.customer_data?.financialPlan
    ? parseInt(importedPortfolio.customer_data.financialPlan, 10)
    : undefined;

  const { t } = useTranslation();

  // Read risk values from the working document (English) and convert to Italian for local state
  const storeRisk = constraintsStore((s) => s.workingDocument?.risk);

  const getData = (): RiskActiveValue => {
    if (storeRisk) {
      // Store holds English risk_profile; map to Italian for local display
      const riskProfileIta = storeRisk.risk_profile
        ? riskProfileMapEngToIta(storeRisk.risk_profile as string) || configData.risk_profile.default
        : configData.risk_profile.default;

      const tempFinancialPlan = storeRisk.financial_plan != null
        ? (storeRisk.financial_plan as number)
        : configData.financial_plan.default;

      return {
        financial_plan: tempFinancialPlan,
        risk_profile: riskProfileIta,
        // Read storeRisk.target_var when set (e.g. user typed a non-default VaR);
        // fall back to the plan's central VaR when storeRisk has `null` (the
        // predefiniti baseline carries target_var: null).
        target_var:
          storeRisk.target_var != null
            ? (storeRisk.target_var as number)
            : value_at_risk_limits[tempFinancialPlan].central,
        ...(isRebalancing
          ? {
              target_var_min: storeRisk.target_var_min != null
                ? storeRisk.target_var_min
                : value_at_risk_limits[tempFinancialPlan].min,
              target_var_max: storeRisk.target_var_max != null
                ? storeRisk.target_var_max
                : value_at_risk_limits[tempFinancialPlan].max_verde,
              align_to_plan: storeRisk.align_to_plan != null ? Boolean(storeRisk.align_to_plan) : false,
            }
          : {}),
      };
    }

    return {
      financial_plan: configData.financial_plan.default,
      risk_profile: configData.risk_profile.default,
      target_var: value_at_risk_limits[configData.financial_plan.default].central,
      ...(isRebalancing
        ? {
            target_var_min: value_at_risk_limits[configData.financial_plan.default].min,
            target_var_max: value_at_risk_limits[configData.financial_plan.default].max_verde,
            align_to_plan: false,
          }
        : {}),
    };
  };

  // Derive display values directly from the store on every render.
  // No local state mirror — this is the source of truth for dropdowns,
  // checkboxes, and incoherence/range-validation logic.
  const activeValue: RiskActiveValue = getData();

  // Local text state for the VaR number inputs ONLY.
  // These allow the user to type intermediate values like "13." without the
  // store clobbering their mid-edit text.
  const [varText, setVarText] = useState<string>(() => formatVarText(activeValue.target_var));
  const [varMinText, setVarMinText] = useState<string>(() => formatVarText(activeValue.target_var_min));
  const [varMaxText, setVarMaxText] = useState<string>(() => formatVarText(activeValue.target_var_max));

  // Sync the text inputs from the store whenever the store changes externally
  // (Reset, profile apply, plan change via dropdown, etc.).
  useEffect(() => {
    setVarText(formatVarText(activeValue.target_var));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValue.target_var]);

  useEffect(() => {
    setVarMinText(formatVarText(activeValue.target_var_min));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValue.target_var_min]);

  useEffect(() => {
    setVarMaxText(formatVarText(activeValue.target_var_max));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValue.target_var_max]);

  const financialPlanMapping = {
    Cauto: { max: 2, min: 1 },
    Prudente: { max: 4, min: 3 },
    Bilanciato: { max: 6, min: 5 },
    Dinamico: { max: 8, min: 7 },
  };

  const onFinancialPlanChange = (value: string) => {
    const currentRiskProfile = activeValue.risk_profile;
    const newPlan = Number(value);
    const central = value_at_risk_limits[newPlan].central;

    if (isRebalancing) {
      // Rebalancing: risk profile is read-only, never auto-update it.
      // If plan > profile range, the incoherence warning will show.
      const newLimits = value_at_risk_limits[newPlan];
      const updates: Record<string, unknown> = {
        financial_plan: newPlan,
        // target_var not written in rebalancing (it's not used directly)
      };

      if (activeValue.align_to_plan) {
        updates.target_var_min = parseFloat((central - 0.25).toFixed(2));
        updates.target_var_max = parseFloat((central + 0.25).toFixed(2));
      } else {
        updates.target_var_min = newLimits.min;
        updates.target_var_max = newLimits.max_verde;
      }

      constraintsStore.getState().applyGroupEdits('risk', updates);
    } else if (newPlan > financialPlanMapping[currentRiskProfile]?.max) {
      // Builder: auto-adjust risk profile to match the new plan
      let newRiskProfile = Object.keys(financialPlanMapping).find(
        (key) => newPlan <= financialPlanMapping[key].max,
      );
      if (!newRiskProfile) {
        newRiskProfile = Object.keys(financialPlanMapping).find(
          (key) => newPlan <= financialPlanMapping[key].max + 1,
        );
      }

      constraintsStore.getState().applyGroupEdits('risk', {
        financial_plan: newPlan,
        risk_profile: riskProfileMapItaToEng(String(newRiskProfile || currentRiskProfile)),
        // Plan changes always produce target_var = plan's central → store null
        target_var: null,
      });
    } else {
      constraintsStore.getState().applyGroupEdits('risk', {
        financial_plan: newPlan,
        // Plan changes always produce target_var = plan's central → store null
        target_var: null,
      });
    }
  };

  const onRiskProfileChange = (value: string) => {
    const financialPlanMax = financialPlanMapping[value]?.min;
    if (activeValue.financial_plan > financialPlanMax) {
      constraintsStore.getState().applyGroupEdits('risk', {
        financial_plan: financialPlanMax,
        risk_profile: riskProfileMapItaToEng(String(value)),
        target_var: null,
      });
    } else {
      constraintsStore.getState().applyGroupEdits('risk', {
        risk_profile: riskProfileMapItaToEng(String(value)),
        target_var: null,
      });
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setVarText(inputValue); // always update text for typing UX

    // Parse and write to store only when we have a valid number.
    // `parseValue` returns a VALIDATED STRING (it's used for input-text
    // display, not numeric storage) — despite its name. The previous
    // `parsed as number` cast was a compile-time lie that left the store
    // holding string values for `target_var`, which later showed up in
    // saved profiles and tripped the document diff against the agent's
    // numeric metadata. Coerce explicitly here so the store always holds
    // a number for this field.
    const parsed = parseValue(e.target, configData);
    if (parsed === '' || parsed === null) return;
    const numericValue = parseFloat(String(parsed));
    if (!Number.isFinite(numericValue)) return;
    let limitedValue = numericValue;
    if (limitedValue > value_at_risk_limits[activeValue.financial_plan].max) {
      limitedValue = value_at_risk_limits[activeValue.financial_plan].max;
    }
    const planCentral = value_at_risk_limits[activeValue.financial_plan]?.central;
    const targetVarToStore =
      typeof planCentral === 'number' && Math.abs(limitedValue - planCentral) < 1e-6
        ? null
        : limitedValue;
    constraintsStore.getState().applyGroupEdits('risk', { target_var: targetVarToStore });
  };

  // Handler for rebalancing VaR min/max inputs
  const handleVarMinMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id;
    let inputValue = e.target.value;

    // Allow decimal input
    inputValue = inputValue.replace(',', '.');
    inputValue = inputValue.replace(/[^0-9.]/g, '');

    // Validate float format
    if (inputValue !== '' && !/^\d*\.?\d*$/.test(inputValue)) {
      return;
    }

    // Cap to plan's max Giallo value
    if (inputValue !== '' && !inputValue.endsWith('.')) {
      const numValue = parseFloat(inputValue);
      const maxAllowed = value_at_risk_limits[activeValue.financial_plan]?.max || 100;
      if (!isNaN(numValue) && numValue > maxAllowed) {
        inputValue = String(maxAllowed);
      }
    }

    // Update local text state for typing UX
    if (id === 'target_var_min') {
      setVarMinText(inputValue);
    } else {
      setVarMaxText(inputValue);
    }

    // Write to store when we have a complete valid number
    if (inputValue !== '' && !inputValue.endsWith('.')) {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue)) {
        // Typing in either input disables align_to_plan (existing behaviour).
        // We write `null` (not `false`) for the off-state — `null` is the
        // canonical "absent" value, matches the predefiniti baseline, and
        // avoids the null-vs-false drift that would falsely flag the
        // savable-fields diff on Salva.
        constraintsStore.getState().applyGroupEdits('risk', {
          align_to_plan: null,
          [id]: numValue,
        });
      }
    } else if (inputValue === '') {
      // Clear the field (same off-state convention as above).
      constraintsStore.getState().applyGroupEdits('risk', {
        align_to_plan: null,
        [id]: null,
      });
    }
  };

  // Handler for "Allinea al piano" checkbox
  const handleAlignToPlanChange = (checked: boolean) => {
    const central = value_at_risk_limits[activeValue.financial_plan].central;

    if (checked) {
      constraintsStore.getState().applyGroupEdits('risk', {
        align_to_plan: true,
        target_var_min: parseFloat((central - 0.25).toFixed(2)),
        target_var_max: parseFloat((central + 0.25).toFixed(2)),
      });
    } else {
      // Uncheck writes `null` (not `false`) for the off-state — `null` is
      // the canonical "absent" value, matches predefiniti, and avoids
      // null-vs-false drift in the savable-fields diff. The JSX
      // `checked={!!activeValue.align_to_plan}` and the `getData()`
      // fallback both already treat `null` as unchecked, so the UI is
      // unaffected.
      constraintsStore.getState().applyGroupEdits('risk', {
        align_to_plan: null,
        target_var_min: value_at_risk_limits[activeValue.financial_plan].min,
        target_var_max: value_at_risk_limits[activeValue.financial_plan].max_verde,
      });
    }
  };

  // Check if risk profile level < financial plan level (incoherent)
  const isIncoherent = (() => {
    const riskProfile = activeValue.risk_profile;
    const plan = activeValue.financial_plan;
    if (!riskProfile || !plan) return false;
    const profileRange = financialPlanMapping[riskProfile];
    if (!profileRange) return false;
    return plan > profileRange.max;
  })();

  // Check if VaR min > VaR max (invalid range)
  const isVarRangeInvalid = (() => {
    if (!isRebalancing) return false;
    const varMin = parseFloat(String(varMinText));
    const varMax = parseFloat(String(varMaxText));
    // Only validate if both fields have valid numeric values
    if (isNaN(varMin) || isNaN(varMax)) return false;
    return varMin > varMax;
  })();

  // Get PTF VaR value for rebalancing display
  const getPtfVarValue = () => {
    if (ptfThread?.risk?.target_var != null) {
      return formatItalianNumber(ptfThread.risk.target_var * 100, 2);
    }
    return '-';
  };

  // Builder mode: original layout
  if (!isRebalancing) {
    return (
      <>
        <div style={{ opacity: activeValue.risk_profile ? 1 : 1, marginTop: 0 }}>
          <Dropdown
            label={t('constraints.financial_plan')}
            options={configData.financial_plan.values.map(String)}
            value={activeValue.financial_plan?.toString()}
            onChange={onFinancialPlanChange}
            fieldPath="risk.financial_plan"
          />
        </div>
        <div style={{ opacity: activeValue.financial_plan ? 1 : 1, marginTop: 10 }}>
          <Dropdown
            label={t('constraints.risk_profile')}
            options={configData.risk_profile.values}
            value={activeValue.risk_profile}
            onChange={onRiskProfileChange}
            fieldPath="risk.risk_profile"
          />
        </div>
        <div
          style={{ color: colorsConfig.finecoBlack, fontSize: 14, marginTop: 10 }}
        >
          <GridItem
            cd={configData}
            cv1={'target_var'}
            cv2={'VaR_max'}
            notEditableValue={{
              value: value_at_risk_limits[activeValue.financial_plan].max,
              label: 'VaR Max',
            }}
            fv={{ ...activeValue, target_var: varText }}
            ohi={handleInputChange}
            dataKey="risk"
          />
        </div>
      </>
    );
  }

  // Rebalancing mode: new layout with min/max VaR
  const currentLimits = value_at_risk_limits[activeValue.financial_plan];
  const varMaxPiano = currentLimits?.max;

  return (
    <>
      {/* Piano finanziario + Allinea al piano + VaR max piano */}
      <div style={{ marginTop: 0 }}>
        <p style={{ fontSize: 12, color: '#70767d' }}>
          {t('constraints.financial_plan')}
        </p>
        <div className={mainStyles.gridContainerRisk1x3}>
          <div className={mainStyles.gridItem}>
            <Dropdown
              label=""
              options={configData.financial_plan.values
                .filter((val) => val <= (ssoFinancialPlan ?? (constraintsStore.getState().baselineDocument()?.risk?.financial_plan as number | undefined) ?? (activeValue.financial_plan as number)))
                .map(String)}
              value={activeValue.financial_plan?.toString()}
              onChange={onFinancialPlanChange}
              fieldPath="risk.financial_plan"
            />
          </div>
          <div className={mainStyles.gridItem} style={{ display: 'flex', alignItems: 'center', paddingBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="align_to_plan"
                checked={!!activeValue.align_to_plan}
                onChange={(e) => handleAlignToPlanChange(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: colorsConfig.finecoBluePrimary }}
              />
              <label
                htmlFor="align_to_plan"
                style={{ fontSize: 12, color: colorsConfig.finecoBlack, cursor: 'pointer' }}
              >
                Allinea al piano
              </label>
            </div>
          </div>
          <div className={mainStyles.gridItem} style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 12, color: '#70767d' }}>VaR max piano</span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{varMaxPiano}%</div>
          </div>
        </div>
      </div>

      {/* Profilo di rischio (read-only in rebalancing) */}
      <div style={{ marginTop: 10 }}>
        <p style={{ fontSize: 12, color: '#70767d' }}>
          {t('constraints.risk_profile')}
        </p>
        <p style={{ fontSize: 14, color: colorsConfig.finecoBlack, paddingLeft: 4 }}>
          {activeValue.risk_profile || '-'}
        </p>
      </div>

      {/* VaR min / VaR max / PTF row */}
      <div style={{ color: colorsConfig.finecoBlack, fontSize: 14, marginTop: 16 }}>
        <div className={mainStyles.gridContainerRisk1x3}>
          {/* VaR min % */}
          <div className={mainStyles.gridItem}>
            <div className="flex items-center">
              <div className={mainStyles.minMax}>VaR min %</div>
              <input
                id="target_var_min"
                value={varMinText}
                onChange={handleVarMinMaxChange}
                disabled={!!activeValue.align_to_plan}
                style={{
                  fontSize: 14,
                  borderRadius: 6,
                  border: '1px solid ' + colorsConfig.gray2,
                  color: colorsConfig.FinecoBlack,
                  margin: 0,
                  padding: 10,
                  width: '100%',
                  cursor: activeValue.align_to_plan ? 'not-allowed' : 'text',
                  backgroundColor: activeValue.align_to_plan ? '#f5f5f5' : '#ffffff',
                }}
              />
            </div>
          </div>

          {/* VaR max % */}
          <div className={mainStyles.gridItem}>
            <div className="flex items-center">
              <div className={mainStyles.minMax}>VaR max %</div>
              <input
                id="target_var_max"
                value={varMaxText}
                onChange={handleVarMinMaxChange}
                disabled={!!activeValue.align_to_plan}
                style={{
                  fontSize: 14,
                  borderRadius: 6,
                  border: '1px solid ' + colorsConfig.gray2,
                  color: colorsConfig.FinecoBlack,
                  margin: 0,
                  padding: 10,
                  width: '100%',
                  cursor: activeValue.align_to_plan ? 'not-allowed' : 'text',
                  backgroundColor: activeValue.align_to_plan ? '#f5f5f5' : '#ffffff',
                }}
              />
            </div>
          </div>

          {/* PTF */}
          <div className={mainStyles.gridItem}>
            <div className={mainStyles.ptf}>
              <div className={mainStyles.ptfTitle}>PTF</div>
              <div className={mainStyles.ptfValue}>{getPtfVarValue()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert: incoherent plan/profile */}
      {isIncoherent && (
        <div style={{ marginTop: 12, marginBottom: 4 }}>
          <IconNote
            type="attention"
            title="Attenzione"
            body="Piano finanziario incoerente con il profilo"
          />
        </div>
      )}

      {/* Alert: VaR min > VaR max */}
      {isVarRangeInvalid && (
        <div style={{ marginTop: 12, marginBottom: 4 }}>
          <IconNote
            type="attention"
            title="Attenzione"
            body="VaR min % non può essere maggiore di VaR max %"
          />
        </div>
      )}

      {/* Bottom spacer — only when no warnings are visible */}
      {!isIncoherent && !isVarRangeInvalid && <div style={{ height: 24 }} />}
    </>
  );
}
